<template>
  <el-input
    v-mask="'###-###-####'"
    :value="displayValue"
    @input="handleInput"
    @blur="onBlur"
    class="el-input-group el-input-group--prepend"
  >
    <template #prepend>
      <el-dropdown placement="bottom">
        <div>
          <img class="flag" :src="selectedCountry.flagImage" alt="flag" />
          <div class="text-flag">{{ selectedCountry.code }}</div>
        </div>
        <el-dropdown-menu>
          <el-dropdown-item v-for="(country, index) in countries">
            <div class="flex items-center justify-center" @click="handleDropdownClick(country)">
              <img class="inline-block flag" :src="country.flagImage" alt="flag" />
              <div class="inline-block text-flag">{{ country.code }}</div>
            </div>
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </template>
  </el-input>
</template>

<script>
import usFlag from '@/assets/images/united-states.png'
import canadaFlag from '@/assets/images/canada.png'
import { getPhoneNumberCountry } from '@/utilities/phoneNumberFormat'

export default {
  props: ['value'],
  data() {
    return {
      select: '',
      manualCountry: null,
      countries: [
        {
          id: 'unitedStates',
          iso: 'US',
          name: 'United States',
          code: '+1',
          flagImage: usFlag
        },
        {
          id: 'canada',
          iso: 'CA',
          name: 'Canada',
          code: '+1',
          flagImage: canadaFlag
        }
      ]
    }
  },
  computed: {
    /**
     * HERA-8859: Derive the flag from the bound value so a saved number keeps
     * showing the country it actually belongs to after a reload, instead of
     * always resetting to the US flag. The country is not persisted, so the
     * stored number is the only source of truth available.
     *
     * While the value is empty or still incomplete no country can be resolved,
     * so the user's dropdown choice stands and United States remains the
     * default. Once the number resolves, it wins over the dropdown: the number
     * is what gets saved, so the flag must agree with it.
     */
    selectedCountry() {
      const defaultCountry = this.countries.find((country) => country.id === 'unitedStates')
      const iso = getPhoneNumberCountry(this.value)
      if (iso) {
        // PR and VI are valid NANP numbers with no flag of their own here, so
        // they fall back to the US flag alongside any other resolved country.
        return this.countries.find((country) => country.iso === iso) || defaultCountry
      }
      return this.manualCountry || defaultCountry
    },
    /**
     * HERA-8230: When the bound value already includes a country code
     * (e.g. "+1-656-443-6547" or "+16564436547"), strip it so the input
     * doesn't render the country code twice (once in the prepend dropdown,
     * once in the input itself). Always display just the national number
     * formatted as XXX-XXX-XXXX. The v-mask will format on user input.
     */
    displayValue() {
      const raw = this.value
      if (raw == null || raw === '') return raw
      const digits = String(raw).replace(/\D/g, '')
      // 11+ digits with leading 1 → drop the country-code "1"
      const national = digits.length === 11 && digits.startsWith('1')
        ? digits.slice(1)
        : digits.length > 10 && digits.startsWith('1')
          ? digits.slice(1, 11)
          : digits.slice(0, 10)
      if (national.length === 0) return ''
      let formatted = national.substring(0, 3)
      if (national.length > 3) formatted += '-' + national.substring(3, 6)
      if (national.length > 6) formatted += '-' + national.substring(6, 10)
      return formatted
    }
  },
  methods: {
    handleInput(e) {
      // Filter out non-numeric characters to prevent text input when replacing selected numbers
      const numericOnly = e.replace(/[^0-9]/g, '')
      
      // Format the numeric input as XXX-XXX-XXXX
      let formatted = ''
      if (numericOnly.length > 0) {
        formatted = numericOnly.substring(0, 3)
        if (numericOnly.length > 3) {
          formatted += '-' + numericOnly.substring(3, 6)
        }
        if (numericOnly.length > 6) {
          formatted += '-' + numericOnly.substring(6, 10)
        }
      }
      
      // Limit to 10 digits maximum
      if (numericOnly.length <= 10) {
        this.$emit('input', formatted)
      }
    },
    handleDropdownClick(country) {
      this.manualCountry = country
    },
    onBlur() {
      this.$emit('blur');
    },
  },

}
</script>

<style>

 .el-input-group__append, .el-input-group__prepend {
    background-color: #fff !important;
}

  .flag {
    width: 20px;
    height: 20px;
    float: left;
    margin-right: 5px;
    margin-left: 5px;
  }

  .text-flag{
    padding-right:37px; 
  }
</style>